<template>
  <div>
    <Nav></Nav>
    <b-container fluid="xl">
      <b-row class="mt-3 mb-2" align-v="center">
        <b-col><h1 class="mb-0">Marketing → QR de descarga</h1></b-col>
      </b-row>

      <b-row class="mb-3" align-v="end">
        <b-col md="3">
          <b-form-group label="Desde" class="mb-0">
            <b-form-input v-model="from" type="date" size="sm"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Hasta" class="mb-0">
            <b-form-input v-model="to" type="date" size="sm"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Tipo" class="mb-0">
            <b-form-select v-model="qrType" :options="typeOptions" size="sm"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-button variant="primary" size="sm" block @click="load" :disabled="loading">
            <b-spinner v-if="loading" small></b-spinner> Buscar
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col v-for="k in kpis" :key="k.label" cols="6" md="3" lg="2">
          <b-card no-body class="mb-2">
            <b-card-body class="py-2 px-3">
              <div class="text-uppercase text-muted" style="font-size: .7rem;">{{ k.label }}</div>
              <div class="h4 mb-0">{{ k.value }}</div>
              <small v-if="k.hint" class="text-muted">{{ k.hint }}</small>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-card v-if="rows.length" no-body class="mb-3">
        <b-card-header class="py-2 d-flex justify-content-between align-items-center">
          <strong>{{ metric === "scans" ? "Escaneos" : "Visitantes" }} por día y origen</strong>
          <b-form-radio-group v-model="metric" :options="metricOptions" buttons size="sm"
            button-variant="outline-secondary"></b-form-radio-group>
        </b-card-header>
        <b-card-body class="py-2">
          <BarChart chart-id="qr-scans-by-day" :labels="chart.labels" :datasets="chart.datasets"
            :options="chartOptions" :height="280" />
        </b-card-body>
      </b-card>

      <b-card v-if="porSucursal.labels.length" no-body class="mb-3">
        <b-card-header class="py-2">
          <strong>Participación por sucursal</strong>
          <small class="text-muted ml-2">
            {{ metric === "scans" ? "Escaneos" : "Visitantes" }} de QR de sucursal.
            <span v-if="porSucursal.sinSucursal">{{ porSucursal.sinSucursal }} de otros orígenes (redes, directo) no se incluyen.</span>
          </small>
        </b-card-header>
        <b-card-body class="py-2">
          <BarChart chart-id="qr-scans-by-sucursal" :labels="porSucursal.labels" :datasets="porSucursal.datasets"
            :options="sucursalOptions" :height="porSucursal.labels.length * 30 + 40" />
        </b-card-body>
      </b-card>

      <b-alert v-if="!loading && !rows.length" show variant="info">
        Sin escaneos en el periodo. Verifica que los QR apunten a
        <code>/download?type=…</code> y que el BFF esté publicando a central.
      </b-alert>

      <b-card v-else no-body>
        <b-card-header class="py-2">
          <strong>Detalle</strong>
          <small class="text-muted ml-2">
            "Visitantes" son únicos aproximados: hash de IP + navegador + día, sin guardar la IP.
          </small>
        </b-card-header>
        <b-table small striped hover responsive :items="rows" :fields="fields" :busy="loading">
          <template #cell(conversion_pct)="d">
            <span :class="d.item.conversion_pct > 0 ? '' : 'text-muted'">
              {{ Number(d.item.conversion_pct).toFixed(1) }}%
            </span>
          </template>
          <template #cell(qr_type)="d">
            <b-badge :variant="typeVariant(d.item.qr_type)">{{ d.item.qr_type }}</b-badge>
            <small v-if="d.item.sucursal" class="ml-1">{{ d.item.sucursal }}</small>
          </template>
        </b-table>
      </b-card>

      <b-alert v-if="sinAtribucion" show variant="warning" class="mt-3 py-2">
        <small>
          Instalaciones y registros salen en cero porque todavía no está conectado el
          proveedor de atribución diferida. El conteo de escaneos sí es exacto.
        </small>
      </b-alert>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../../components/Nav.vue";
import BarChart from "../../components/Graphs/BarChart.vue";

// Color fijo por origen (paleta categórica validada, orden fijo): un filtro de tipo no
// repinta los demás. Orígenes no previstos van al gris neutro de "Otro".
const ORIGINS = [
  { key: "meta", label: "Meta", color: "#2a78d6" },
  { key: "tiktok", label: "TikTok", color: "#eb6834" },
  { key: "sucursal", label: "Sucursal", color: "#1baf7a" },
  { key: "direct", label: "Directo", color: "#eda100" }
];
const OTHER_ORIGIN = { key: "_other", label: "Otro", color: "#8a8985" };

export default {
  name: "qrDescargas",
  components: { Nav, BarChart },
  data() {
    const hoy = new Date();
    const hace30 = new Date(hoy.getTime() - 30 * 86400000);
    const iso = (d) => d.toISOString().slice(0, 10);
    return {
      loading: false,
      from: iso(hace30),
      to: iso(hoy),
      qrType: "",
      metric: "scans",
      metricOptions: [
        { value: "scans", text: "Escaneos" },
        { value: "visitors", text: "Visitantes" }
      ],
      rows: [],
      summary: {},
      byType: {},
      typeOptions: [
        { value: "", text: "Todos" },
        { value: "meta", text: "Meta" },
        { value: "tiktok", text: "TikTok" },
        { value: "sucursal", text: "Sucursal" },
        { value: "direct", text: "Directo" }
      ],
      fields: [
        { key: "day", label: "Día", sortable: true },
        { key: "qr_type", label: "Origen" },
        { key: "campaign", label: "Campaña" },
        { key: "scans", label: "Escaneos", sortable: true },
        { key: "visitors", label: "Visitantes", sortable: true },
        { key: "android", label: "Android", sortable: true },
        { key: "ios", label: "iOS", sortable: true },
        { key: "other", label: "Otro" },
        { key: "installs", label: "Instalaciones", sortable: true },
        { key: "registers", label: "Registros", sortable: true },
        { key: "conversion_pct", label: "Conv." }
      ]
    };
  },
  computed: {
    kpis() {
      const s = this.summary || {};
      const total = s.scans || 0;
      const pct = (n) => (total > 0 ? ((n / total) * 100).toFixed(0) + "%" : "—");
      return [
        { label: "Escaneos", value: total },
        { label: "Visitantes", value: s.visitors || 0, hint: "únicos aprox." },
        { label: "Android", value: s.android || 0, hint: pct(s.android || 0) },
        { label: "iOS", value: s.ios || 0, hint: pct(s.ios || 0) },
        { label: "Instalaciones", value: s.installs || 0 },
        { label: "Registros", value: s.registers || 0 }
      ];
    },
    // Días del rango consultado (incluye días sin escaneos en cero) × origen.
    chart() {
      const days = [];
      const start = new Date(this.from + "T00:00:00Z");
      const end = new Date(this.to + "T00:00:00Z");
      for (let d = start; d <= end; d = new Date(d.getTime() + 86400000)) {
        days.push(d.toISOString().slice(0, 10));
      }
      const idx = {};
      days.forEach((d, i) => (idx[d] = i));
      const known = {};
      ORIGINS.forEach((o) => (known[o.key] = o));
      const sums = {};
      this.rows.forEach((r) => {
        const day = String(r.day).slice(0, 10);
        if (idx[day] === undefined) return;
        const origin = known[r.qr_type] ? r.qr_type : OTHER_ORIGIN.key;
        if (!sums[origin]) sums[origin] = days.map(() => 0);
        sums[origin][idx[day]] += Number(r[this.metric]) || 0;
      });
      const present = ORIGINS.concat([OTHER_ORIGIN]).filter((o) => sums[o.key]);
      const datasets = present.map((o, i) => ({
        label: o.label,
        data: sums[o.key],
        backgroundColor: o.color,
        hoverBackgroundColor: o.color,
        borderColor: "#ffffff",
        borderWidth: { top: 2 },
        borderSkipped: "bottom",
        borderRadius: i === present.length - 1 ? { topLeft: 4, topRight: 4 } : 0,
        maxBarThickness: 28
      }));
      const fmt = (d) => new Date(d + "T00:00:00Z").toLocaleDateString("es-MX", { day: "numeric", month: "short", timeZone: "UTC" });
      return { labels: days.map(fmt), datasets };
    },
    chartOptions() {
      const ink = "#52514e";
      const grid = "#ecebe8";
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: { stacked: true, grid: { display: false }, ticks: { color: ink, maxRotation: 0, autoSkipPadding: 12 } },
          y: { stacked: true, beginAtZero: true, grid: { color: grid }, border: { display: false },
            ticks: { color: ink, precision: 0 } }
        },
        plugins: {
          legend: { display: this.chart.datasets.length > 1, position: "top", align: "start",
            labels: { color: ink, boxWidth: 12, boxHeight: 12, usePointStyle: true, pointStyle: "rectRounded" } },
          tooltip: {
            itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
            filter: (item) => item.raw > 0,
            callbacks: {
              footer: (items) => "Total: " + items.reduce((s, it) => s + (Number(it.raw) || 0), 0)
            }
          }
        }
      };
    },
    // Total del periodo por sucursal, de mayor a menor; la etiqueta lleva el % del total
    // de sucursales para leer la participación sin depender del color.
    porSucursal() {
      const totals = {};
      let sinSucursal = 0;
      this.rows.forEach((r) => {
        const v = Number(r[this.metric]) || 0;
        if (!r.sucursal_id) {
          sinSucursal += v;
          return;
        }
        const name = r.sucursal || "Sucursal " + r.sucursal_id;
        totals[name] = (totals[name] || 0) + v;
      });
      const entries = Object.keys(totals).map((k) => [k, totals[k]]).sort((a, b) => b[1] - a[1]);
      const total = entries.reduce((s, e) => s + e[1], 0);
      const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
      return {
        sinSucursal,
        labels: entries.map((e) => `${e[0]} · ${pct(e[1])}%`),
        datasets: [{
          label: this.metric === "scans" ? "Escaneos" : "Visitantes",
          data: entries.map((e) => e[1]),
          backgroundColor: "#2a78d6",
          hoverBackgroundColor: "#2a78d6",
          borderRadius: { topRight: 4, bottomRight: 4 },
          borderSkipped: "start",
          maxBarThickness: 18
        }]
      };
    },
    sucursalOptions() {
      const ink = "#52514e";
      return {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { beginAtZero: true, grid: { color: "#ecebe8" }, border: { display: false },
            ticks: { color: ink, precision: 0 } },
          y: { grid: { display: false }, ticks: { color: ink } }
        },
        plugins: { legend: { display: false } }
      };
    },
    sinAtribucion() {
      return (this.summary.scans || 0) > 0 && (this.summary.installs || 0) === 0;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    typeVariant(t) {
      return { meta: "primary", tiktok: "dark", sucursal: "success" }[t] || "secondary";
    },
    load() {
      this.loading = true;
      axios
        .get("/report/qr-scans", { params: { from: this.from, to: this.to, type: this.qrType } })
        .then((r) => {
          const d = r.data || {};
          this.rows = d.rows || [];
          this.summary = d.summary || {};
          this.byType = d.by_type || {};
        })
        .catch((e) => {
          const msg = (e.response && e.response.data) || e.message || "Error desconocido";
          this.$bvToast.toast(typeof msg === "string" ? msg : JSON.stringify(msg), {
            title: "Error", variant: "danger", autoHideDelay: 6000
          });
        })
        .finally(() => (this.loading = false));
    }
  }
};
</script>
